import React, { useCallback, useRef, useState } from 'react';
import { Button, Row, Col } from 'reactstrap';
import { connect } from 'react-redux';
import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import {
  Intl, LocalTable, ModalInfo,
} from 'components';
import bg from 'assets/img/step3Bg.png';
import SettingsActions from 'store/reducers/Settings';
import { useFetch } from 'hooks';
import ListPhoneNumbersService from 'api/ListPhoneNumbers';
import CampaignService from 'api/Campaign';
import { handlePromise } from 'utils';
import GenericContactModal from '../GenericContactModal';

const ContactFavouriteModal = ( {
  campaignId, values, open, onAddContacts, onClose, toggleErrorAlert, toggleLoading,
} ) => {
  const [contacts, setContacts] = useState( _.filter( values.contacts || [], { type: 'favourite' } ) );
  const [invalidContactsModalOpened, setInvalidContactsModalOpened] = useState( false );
  const [localError, setLocalErrors] = useState( null );
  const invalidContactsRef = useRef( [] );

  const [data, dataLoading, setData] = useFetch( {
    initialState: [],
    promise: () => ListPhoneNumbersService.getListPhoneNumbers( { limit: 0 } ),
    format: ( dataToFormat ) => _.map( dataToFormat.data,
      ( list ) => ( { ...list, selected: _.findIndex( contacts, { owner: list.id } ) !== -1 } ) ),
    toggleErrorAlert,
    conditional: open,
    deps: [open],
    reInit: true,
  } );

  const toggleSelected = useCallback( ( row ) => {
    if ( row.original.id ) {
      const newContacts = _.cloneDeep( contacts );
      const contactIndex = _.findIndex( newContacts, { id: row.original.id } );
      if ( contactIndex !== -1 ) {
        newContacts[contactIndex].selected = !newContacts[contactIndex].selected;
      }
      setContacts( newContacts );
    }
  }, [contacts] );

  const toggleInvalidContactsModal = useCallback( ( invalidContacts = [] ) => {
    invalidContactsRef.current = invalidContacts;
    setInvalidContactsModalOpened( !invalidContactsModalOpened );
  }, [invalidContactsModalOpened] );

  const toggleSelectedList = useCallback( async ( row ) => {
    if ( row.original.id ) {
      console.log("here");
      const newLists = _.cloneDeep( data );
      const listIndex = _.findIndex( newLists, { id: row.original.id } );
      let newContacts = _.cloneDeep( contacts );

      if ( listIndex !== -1 ) {
        const item = newLists[listIndex];
        if ( !item.selected ) {
          toggleLoading( true );
          const [, response, responseData] = await handlePromise(
            CampaignService.validatePhone( campaignId, {
              phoneNumbers: _.map( item.phoneNumbers, ( contact ) => ( {
                ...contact,
                phoneNumberCountryCode: values.phoneNumberCountryCode,
              } ) ),
            } ),
          );
          toggleLoading( false );
          if ( !response.ok ) return setLocalErrors( 'apiError' );

          const invalidPhones = _.filter( responseData, { valid: false } );
          const validPhones = _.filter( responseData, { valid: true } );
          newContacts = newContacts.concat( _.map( validPhones, ( phone ) => ( {
            ...phone,
            owner: item.id,
            id: uuidv4(),
            selected: true,
            type: 'favourite',
          } ) ) );
          if ( invalidPhones.length ) {
            toggleInvalidContactsModal( invalidPhones );
          }
        } else {
          newContacts = _.filter( newContacts, ( contact ) => contact.owner !== item.id );
        }
        newLists[listIndex].selected = !item.selected;
        setData( newLists );
        setContacts( newContacts );
      }
    }
  }, [contacts, data, campaignId, values.phoneNumberCountryCode,
    setData, toggleInvalidContactsModal, toggleLoading] );

  return (
    <ModalInfo
      title="contactsFavouriteModalTitle"
      size="lg"
      open={open}
      isLoading={dataLoading}
      onClose={onClose}
      footer={(
        <Button
          color="app-default"
          size="app-sm"
          className="font-weight-700"
          type="button"
          onClick={() => onAddContacts( contacts, 'favourite' )}
        >
          <Intl id="save" />
        </Button>
      )}
    >
      <>
        {!!localError
        && <div className="error text-md text-center"><Intl id={localError} /></div>}

        <Row className="my-3">
          <Col md={7} className="d-flex flex-column justify-content-center pl-md-0" style={{ minHeight: 260 }}>
            <img
              src={bg}
              alt="..."
              className="campaign-wizard-triangle img-fluid d-none d-lg-block"
              style={{ minHeight: 260 }}
            />

            <div className="w-100 d-flex">
              <Col md={10} xs={12} className="m-auto campaign-wizard-table campaign-wizard-table-w-100 pl-0 pr-0 pr-md-3">
                <LocalTable
                  data={data.length < 5
                    ? data.concat( new Array( 5 - data.length ).fill( { empty: true } ) )
                    : data}
                  manualPagination
                  onlyTable
                  getRowProps={( row ) => ( {
                    onClick: () => toggleSelectedList( row ),
                    className: row.original.selected ? 'bg-orange-6' : '',
                  } )}
                  columns={[
                    {
                      Header: <Intl
                        id="selectedLists"
                        values={{
                          selected: _.filter( data, { selected: true } ).length,
                          total: data.length,
                        }}
                      />,
                      disableTranslate: true,
                      accessor: 'name',
                      style: { textAlign: 'left' },
                    },
                  ]}
                />
              </Col>
            </div>
          </Col>

          <Col md={5} className="mt-3 mt-md-0 d-flex pl-md-0 align-items-md-center campaign-wizard-table campaign-wizard-table-w-100">
            <LocalTable
              data={contacts.length < 5
                ? contacts.concat( new Array( 5 - contacts.length ).fill( { empty: true } ) )
                : contacts}
              manualPagination
              onlyTable
              getRowProps={( row ) => ( {
                onClick: () => toggleSelected( row ),
                className: row.original.selected ? 'bg-orange-6' : '',
              } )}
              columns={[
                {
                  Header: 'nameOrId',
                  accessor: 'nameId',
                  customWidth: 110,
                },
                {
                  Header: <Intl
                    id="phonesCount"
                    values={{
                      selected: _.filter( contacts, { selected: true } ).length,
                      total: contacts.length,
                    }}
                  />,
                  disableTranslate: true,
                  accessor: 'phoneNumber',
                  customWidth: 185,
                },
              ]}
            />
          </Col>
        </Row>

        {invalidContactsModalOpened
        && (
          <GenericContactModal
            contacts={invalidContactsRef.current}
            modalTitle="wrongPhones"
            tableTitle="wrongPhones"
            open={invalidContactsModalOpened}
            onClose={toggleInvalidContactsModal}
            type="error"
          />
        )}
      </>
    </ModalInfo>
  );
};

const mapDispatchToProps = ( {
  toggleLoading: SettingsActions.toggleLoading,
  toggleErrorAlert: SettingsActions.toggleErrorAlert,
} );

export default connect( null, mapDispatchToProps )( ContactFavouriteModal );
